import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import { PublicPortalService } from '../../services/public-portal.service';
import { BillingService } from '../../services/billing.service';
import { TenantSignupRequestCreateDto } from '../../models/tenant-signup';
import { BillingInterval, PlanDto } from '../../models/billing';
import { subdomainValidator } from './subdomain.validator';
import { LanguageService } from '../../services/language.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

/**
 * Public "Become a tenant" onboarding wizard. A three-step flow
 * (Organization → Plan → Contact & review) over the real tenant-signup contract.
 *
 * The backend DTO only stores name/contact/email/phone/subdomain/message, so the
 * chosen organization-type and plan are folded into `message` as notes for the
 * reviewer rather than invented persisted fields.
 */
@Component({
  selector: 'app-join-tenant',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, TranslateModule],
  templateUrl: './join-tenant.component.html',
  styleUrls: ['./join-tenant.component.scss'],
})
export class JoinTenantComponent implements OnInit {
  form: FormGroup;
  step = 1;
  readonly lastStep = 3;
  submitting = false;
  submitted = false;
  errorMessage = '';

  readonly domainSuffix = '.' + (environment.baseDomain || 'menassaty.com');

  // The stored value stays the English label (that's what the API expects); only
  // the chip's caption is localized.
  readonly orgTypes: { value: string; key: string }[] = [
    { value: 'K-12 School', key: 'joinTenant.OrgTypeK12' },
    { value: 'University / College', key: 'joinTenant.OrgTypeUniversity' },
    { value: 'Corporate Training', key: 'joinTenant.OrgTypeCorporate' },
    { value: 'Language School', key: 'joinTenant.OrgTypeLanguage' },
    { value: 'Bootcamp / Academy', key: 'joinTenant.OrgTypeBootcamp' },
    { value: 'Government / NGO', key: 'joinTenant.OrgTypeGovNgo' },
    { value: 'Other', key: 'joinTenant.OrgTypeOther' },
  ];

  /** Localized caption for the selected type, for the order summary. */
  get orgTypeLabel(): string {
    const t = this.orgTypes.find((o) => o.value === this.orgType);
    return t ? this.translate.instant(t.key) : '';
  }
  orgType = '';

  // pricing (real plans)
  plans: PlanDto[] = [];
  readonly currencies = ['EGP', 'SAR', 'USD'];
  selectedCurrency = 'EGP';
  interval: BillingInterval = 1;
  selectedPlanCode = '';

  constructor(
    private fb: FormBuilder,
    private portal: PublicPortalService,
    private billing: BillingService,
    private title: Title,
    private translate: TranslateService,
    private language: LanguageService,
  ) {
    this.form = this.fb.group({
      organizationName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(150)]],
      contactName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(256)]],
      phone: ['', [Validators.maxLength(20)]],
      preferredSubdomain: ['', [Validators.maxLength(63), Validators.pattern('^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$'), subdomainValidator()]],
      message: ['', [Validators.maxLength(2000)]],
    });
  }

  ngOnInit(): void {
    this.title.setTitle('Become a Tenant — Menassaty');
    this.billing.publicPlans().subscribe({
      next: (res) => {
        if (!res.isSuccess || !res.value) return;
        this.plans = [...res.value].sort((a, b) => a.sortOrder - b.sortOrder);
        const available = new Set(this.plans.flatMap((p) => p.prices.map((x) => x.currency)));
        if (!available.has(this.selectedCurrency)) {
          this.selectedCurrency = this.currencies.find((c) => available.has(c)) ?? this.selectedCurrency;
        }
        // Default to the "popular" middle plan when available.
        if (!this.selectedPlanCode && this.plans.length) {
          this.selectedPlanCode = (this.plans[this.isPopularIndex] ?? this.plans[0]).code;
        }
      },
      error: () => {},
    });
  }

  get f() { return this.form.controls; }

  // ── wizard navigation ─────────────────────────────────────────────────────
  private get isPopularIndex(): number { return this.plans.length >= 3 ? 1 : 0; }

  /** Whether the current step's required inputs are valid. */
  get canAdvance(): boolean {
    if (this.step === 1) {
      return this.f['organizationName'].valid && this.f['preferredSubdomain'].valid;
    }
    if (this.step === 2) return true; // plan is optional
    return this.form.valid;
  }

  next(): void {
    if (!this.canAdvance) { this.markStepTouched(); return; }
    if (this.step < this.lastStep) this.step++;
  }
  back(): void { if (this.step > 1) this.step--; }

  private markStepTouched(): void {
    const groups: Record<number, string[]> = {
      1: ['organizationName', 'preferredSubdomain'],
      3: ['contactName', 'email', 'phone'],
    };
    (groups[this.step] ?? []).forEach((k) => this.f[k].markAsTouched());
  }

  // ── subdomain preview ─────────────────────────────────────────────────────
  get subdomainPreview(): string {
    const v = (this.f['preferredSubdomain'].value || '').trim().toLowerCase();
    return (v || 'your-academy') + this.domainSuffix;
  }
  normalizeSubdomain(): void {
    const v = (this.f['preferredSubdomain'].value || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
    this.f['preferredSubdomain'].setValue(v, { emitEvent: false });
  }
  selectOrgType(t: string): void { this.orgType = this.orgType === t ? '' : t; }

  // ── pricing helpers ───────────────────────────────────────────────────────
  // Via the service so CMS/plan copy re-picks the instant the toggle fires.
  private get lang(): string { return this.language.current; }
  private pick(en?: string | null, ar?: string | null): string {
    return (this.lang === 'ar' ? ar || en : en || ar) ?? '';
  }
  setCurrency(c: string): void { this.selectedCurrency = c; }
  setInterval(i: BillingInterval): void { this.interval = i; }
  selectPlan(code: string): void { this.selectedPlanCode = code; }
  priceFor(p: PlanDto): number | null {
    return p.prices.find((x) => x.currency === this.selectedCurrency && x.interval === this.interval)?.amount ?? null;
  }
  isPopular(i: number): boolean { return this.plans.length >= 3 && i === 1; }
  planName(p: PlanDto): string { return this.pick(p.nameEn, p.nameAr); }
  planDescription(p: PlanDto): string { return this.pick(p.descriptionEn, p.descriptionAr); }
  planStudents(p: PlanDto): string {
    const v = p.entitlements.find((e) => e.key === 'maxStudents')?.value;
    if (v == null) return '';
    const n = +v;
    return n < 0
      ? this.translate.instant('portal.UnlimitedStudents')
      : this.translate.instant('portal.UpToStudents', { n });
  }
  get selectedPlanName(): string {
    const p = this.plans.find((x) => x.code === this.selectedPlanCode);
    return p ? this.planName(p) : '—';
  }

  // ── submit ────────────────────────────────────────────────────────────────
  submit(): void {
    this.errorMessage = '';
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.submitting = true;

    const notes: string[] = [];
    if (this.orgType) notes.push(`Organization type: ${this.orgType}`);
    if (this.selectedPlanName && this.selectedPlanName !== '—') notes.push(`Interested plan: ${this.selectedPlanName}`);
    const userMsg = (this.f['message'].value || '').trim();
    if (userMsg) notes.push(userMsg);

    const dto: TenantSignupRequestCreateDto = {
      organizationName: this.f['organizationName'].value,
      contactName: this.f['contactName'].value,
      email: this.f['email'].value,
      phone: this.f['phone'].value || undefined,
      preferredSubdomain: this.f['preferredSubdomain'].value || undefined,
      message: notes.length ? notes.join('\n') : undefined,
    };

    this.portal.submitTenantSignup(dto).subscribe({
      next: (res) => {
        this.submitting = false;
        if (res.isSuccess) this.submitted = true;
        else this.errorMessage = res.errors?.[0] ?? 'Something went wrong. Please try again.';
      },
      error: () => {
        this.submitting = false;
        this.errorMessage = 'Unable to submit your request right now. Please try again later.';
      },
    });
  }

  resetForm(): void {
    this.submitted = false;
    this.step = 1;
    this.orgType = '';
    this.form.reset();
  }
}
